var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { differenceWith, eqProps, prop, } from 'ramda';
import React, { useCallback, useEffect, useRef, useState, } from 'react';
import { useIntl } from 'react-intl';
import { Chip } from '@material-ui/core';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { componentMessages } from '../../component-translations';
import { useDebouncedCallback } from '../Debounce/DebounceCallback';
import { PaperComponent } from './PaperComponent';
import { RenderInput } from './RenderInput';
import { RenderOption } from './RenderOption';
const useStyles = makeStyles({
    fullWidth: {
        width: '100%'
    }
});
const LIMIT = 11;
const DEFAULT_DEBOUNCE = 500;
function MultiAutocomplete(props) {
    const { formatMessage } = useIntl();
    const { initialValues, getOptions, loadingText = formatMessage(componentMessages['autocomplete.loading']), noResultsText = formatMessage(componentMessages['autocomplete.noResults']), moreResultsText = formatMessage(componentMessages['autocomplete.tooManyResults']), placeholder = formatMessage(componentMessages['autocomplete.placeholder']), label = formatMessage(componentMessages['autocomplete.label']), onChange, className, debounceDelay, fullWidth, numberOfResults, useCache = true } = props;
    const classes = useStyles();
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [value, setValue] = useState(initialValues || []);
    const [options, setOptions] = useState([]);
    const [moreResults, setMoreResults] = useState(false);
    const limit = (numberOfResults + 1) || LIMIT;
    const cachedQueries = useRef({});
    const handleSearch = useCallback((term = '', context) => __awaiter(this, void 0, void 0, function* () {
        const foundCacheItem = cachedQueries.current[term];
        const returnedOptions = foundCacheItem ? foundCacheItem : yield getOptions(term || '', limit);
        if (context.isCurrent) {
            const trimmedOptions = returnedOptions.slice(0, limit - 1);
            setMoreResults(returnedOptions.length >= limit);
            setOptions([...trimmedOptions]);
            if (useCache) {
                cachedQueries.current[term] = trimmedOptions;
            }
            setIsLoading(false);
        }
    }), [getOptions, limit, useCache, setIsLoading, setOptions, setMoreResults]);
    useEffect(() => setValue(initialValues || []), [initialValues]);
    useEffect(() => {
        if (!isOpen) {
            return;
        }
        const context = { isCurrent: true };
        handleSearch(searchTerm, context);
        // if a newer updateSearch() is necessary, update context to avoid using stale search results
        return () => context.isCurrent = false;
    }, [searchTerm, isOpen, handleSearch]);
    useEffect(() => {
        if (!value || value.length === 0) {
            return;
        }
        const cmp = (x, y) => x.code === y.code;
        const toAdd = differenceWith(cmp, value, options);
        if (toAdd.length > 0) {
            setOptions((currentValue) => [...value, ...currentValue]);
        }
    }, [value, options, setOptions]);
    const handleDebouncedSearch = useDebouncedCallback((term) => {
        setSearchTerm(term);
    }, debounceDelay || DEFAULT_DEBOUNCE);
    const onAutocompleteChange = (event, newValue) => {
        if (newValue) {
            setValue(newValue);
            onChange(event, newValue ? newValue : []);
        }
    };
    const renderTags = (inputValues, getTagProps) => {
        return inputValues.map((input, index) => (React.createElement(Chip, Object.assign({ key: input.code, variant: 'outlined', label: input.name, size: 'small' }, getTagProps({ index })))));
    };
    const onOpen = () => {
        setIsOpen(true);
        setIsLoading(true);
    };
    const onClosed = () => {
        setIsOpen(false);
    };
    const handleInput = (_, newValue) => {
        setMoreResults(false);
        setIsLoading(true);
        handleDebouncedSearch(newValue);
    };
    const autoCompleteText = () => {
        if (!isLoading && options.length === 0) {
            return noResultsText;
        }
        return loadingText;
    };
    return (React.createElement(Autocomplete, { className: fullWidth ? classes.fullWidth : '', onChange: onAutocompleteChange, multiple: true, options: options, onOpen: onOpen, onClose: onClosed, onInputChange: handleInput, value: value, getOptionLabel: prop('name'), getOptionSelected: eqProps('code'), renderTags: renderTags, noOptionsText: autoCompleteText(), popupIcon: null, PaperComponent: PaperComponent({ moreResults, moreResultsText }), renderInput: RenderInput({ className, label, placeholder }), renderOption: RenderOption() }));
}
export default MultiAutocomplete;
//# sourceMappingURL=MultiAutocomplete.js.map